import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, Flame, ArrowRight, AlertTriangle, Home } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { BRAND_COLORS, STATUS_COLORS, withAlpha } from "../lib/bookingStatus";

/**
 * Feb 2026 · User-Directive „Home-Willkommensgruß erweitern".
 *
 * Kompakte Zwei-Segment-Zeile knapp unter der Concierge-Startseiten-Begrüßung:
 *   • „Heute anstehend"   → Anreisen HEUTE (bookings.to_date === heute)
 *   • „Diese Woche brennt" → rote Objekte + dringende offene Anfragen
 *
 * 26 Sep 2026 · Desync-Fix „Diese Woche brennt": Früher zeigte die Kachel EINE
 * kombinierte Zahl (`redProps + urgentReqs`), verlinkte aber nur auf EINE Liste
 * (Objekte, sobald ein rotes Objekt existierte). Tippte man, zeigte die
 * aufgeklappte Handlungsbedarf-Liste eine kleinere/leere Zahl als die Kachel —
 * „rote Kachel vs. leere Liste". Jetzt hat jede Kategorie ihre EIGENE Zeile mit
 * eigener Zahl und eigenem Sprungziel: die Zahl auf der Kachel entspricht immer
 * exakt der Anzahl in der Liste, die sich beim Tippen öffnet.
 *
 * Die Quelle ist derselbe Endpoint wie für den ConciergeCockpitChips-Widget
 * (`/api/team/concierge-stats`) — damit bleiben Home-Zahlen und Admin-
 * Auslastungstabelle deckungsgleich.
 *
 * Nicht sichtbar für „reinen" Admin ohne Impersonation — dort wird die
 * ConciergeHome ohnehin nicht gerendert.
 */
export default function ConciergeWelcomeRow() {
  const { user, activeConciergeId } = useAuth();
  const [row, setRow] = useState(null);

  const scopeId = activeConciergeId || (user?.role === "concierge" ? user.id : null);

  useEffect(() => {
    if (!scopeId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/team/concierge-stats");
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setRow(list.find((r) => r?.concierge?.id === scopeId) || list[0] || null);
      } catch (e) {
        if (!cancelled) setRow(null);
      }
    })();
    return () => { cancelled = true; };
  }, [scopeId]);

  if (!scopeId) return null;

  const arrivalsToday = row?.arrivals_today ?? 0;
  const redProps = row?.red_count ?? 0;
  const urgentReqs = row?.urgent_open_requests ?? 0;

  // Beide Segmente rendern grundsätzlich (auch bei 0), damit die Zeile ein
  // stabiler „Puls-Streifen" bleibt und dem Concierge Ruhe/Alarm signalisiert.
  return (
    <div
      className="mb-5 flex flex-wrap gap-2 items-stretch"
      data-testid="concierge-welcome-row"
      data-scope={scopeId}
    >
      <WelcomeSegment
        to="/team/calendar"
        icon={CalendarClock}
        label="Heute anstehend"
        count={arrivalsToday}
        emptyText="Kein Termin heute"
        detailText={
          arrivalsToday === 0
            ? undefined
            : arrivalsToday === 1
              ? "1 Anreise heute"
              : `${arrivalsToday} Anreisen heute`
        }
        accent={STATUS_COLORS.blue}
        testid="welcome-segment-today"
      />
      <BurnSegment redProps={redProps} urgentReqs={urgentReqs} />
    </div>
  );
}

/**
 * „Diese Woche brennt" — pro Kategorie eine eigene, klickbare Zeile, deren Zahl
 * exakt der Ziel-Liste entspricht (Objekte → `?filter=attention`, Anfragen →
 * `?filter=urgent`). So kann die Kachel-Zahl nie von der aufgeklappten Liste
 * abweichen.
 */
function BurnSegment({ redProps, urgentReqs }) {
  const burnCount = redProps + urgentReqs;
  const calm = burnCount === 0;
  const accent = calm ? STATUS_COLORS.green : STATUS_COLORS.red;
  return (
    <div
      data-testid="welcome-segment-week"
      data-count={burnCount}
      data-objects={redProps}
      data-requests={urgentReqs}
      className="flex-1 min-w-[240px] flex flex-col justify-center gap-1.5 px-4 py-2.5 bg-white border border-navy/10 transition-colors"
      style={calm ? {} : { borderColor: withAlpha(accent, 0.35), background: withAlpha(accent, 0.04) }}
    >
      <span
        className="label-caps text-[10px] tracking-[0.14em] inline-flex items-center gap-1.5"
        style={{ color: calm ? "rgba(45, 55, 85, 0.55)" : accent }}
      >
        <Flame size={13} strokeWidth={1.7} style={{ color: calm ? "rgba(45, 55, 85, 0.55)" : accent }} />
        Diese Woche brennt
      </span>
      {calm ? (
        <span className="block text-[13px] text-navy-dark/70">
          <span className="font-display text-lg mr-1.5 tabular-nums" style={{ color: BRAND_COLORS.navy }}>0</span>
          Alles ruhig
        </span>
      ) : (
        <div className="flex flex-col gap-0.5">
          {redProps > 0 && (
            <BurnLine
              to="/team/properties?filter=attention"
              icon={Home}
              count={redProps}
              text={redProps === 1 ? "rotes Objekt" : "rote Objekte"}
              accent={accent}
              testid="welcome-week-objects"
            />
          )}
          {urgentReqs > 0 && (
            <BurnLine
              to="/team/requests?filter=urgent"
              icon={AlertTriangle}
              count={urgentReqs}
              text={urgentReqs === 1 ? "dringende Anfrage" : "dringende Anfragen"}
              accent={accent}
              testid="welcome-week-requests"
            />
          )}
        </div>
      )}
    </div>
  );
}

function BurnLine({ to, icon: Icon, count, text, accent, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      data-count={count}
      className="group inline-flex items-center gap-2 text-[13px] text-navy hover:text-gold transition-colors"
    >
      <Icon size={14} strokeWidth={1.7} style={{ color: accent }} className="shrink-0" />
      <span className="font-display text-base tabular-nums" style={{ color: accent }}>{count}</span>
      <span className="text-navy-dark/75 truncate">{text}</span>
      <ArrowRight
        size={12}
        strokeWidth={1.8}
        className="shrink-0 text-navy-dark/25 group-hover:text-gold transition-colors"
      />
    </Link>
  );
}

function WelcomeSegment({ to, icon: Icon, label, count, emptyText, detailText, accent, testid }) {
  const isZero = count === 0;
  const numberColor = isZero ? BRAND_COLORS.navy : accent;
  return (
    <Link
      to={to}
      data-testid={testid}
      data-count={count}
      className="group flex-1 min-w-[240px] inline-flex items-center gap-3 px-4 py-2.5 bg-white border border-navy/10 hover:border-gold transition-colors"
      style={isZero ? {} : { borderColor: withAlpha(accent, 0.35), background: withAlpha(accent, 0.04) }}
    >
      <span
        className="shrink-0 w-8 h-8 flex items-center justify-center"
        style={{
          background: withAlpha(accent, isZero ? 0.06 : 0.14),
          border: `1px solid ${withAlpha(accent, isZero ? 0.15 : 0.3)}`,
        }}
      >
        <Icon size={16} strokeWidth={1.6} style={{ color: numberColor }} />
      </span>
      <span className="flex-1 min-w-0 leading-tight">
        <span
          className="label-caps text-[10px] tracking-[0.14em]"
          style={{ color: isZero ? "rgba(45, 55, 85, 0.55)" : accent }}
        >
          {label}
        </span>
        <span className="block text-[13px] text-navy mt-0.5 truncate">
          <span className="font-display text-lg mr-1.5 tabular-nums" style={{ color: numberColor }}>
            {count}
          </span>
          <span className="text-navy-dark/70">{detailText || emptyText}</span>
        </span>
      </span>
      <ArrowRight
        size={14}
        strokeWidth={1.8}
        className="shrink-0 text-navy-dark/30 group-hover:text-gold transition-colors"
      />
    </Link>
  );
}
